import { COMMON_NAMESPACE } from "constants/i18n-namespaces";
import { useLabelsWithKrn } from "hooks";
import { t } from "i18next";
import { ExtensionField, LookupField } from "types/payable";
import { getLabelWithKrn } from "utilities/helper";
import AddressField from "./AddressField/AddressField";
import { AmountField } from "./AmountField/AmountField";
import { BooleanField } from "./BooleanField";
import { DateField } from "./DateField";
import { DateTimeField } from "./DateTimeField/DateTimeField";
import { EmailAddress } from "./EmailAddress";
import { IntegerField } from "./IntegerField";
import { NonBlankText } from "./NonBlankText";
import { PhoneNumber } from "./PhoneNumber";
import { SelectManyField } from "./SelectManyField/SelectManyField";
import { SelectOne } from "./SelectOneField/SelectOneField";

export interface FieldProps {
  fieldDefinition: ExtensionField | LookupField;
  typeKrn: string | null;
}

export const Field = ({ fieldDefinition: fieldDef, typeKrn }: FieldProps) => {
  const { getLabelsWithKrn } = useLabelsWithKrn()
  const labelSourceWithKrn = getLabelsWithKrn()
  let isEditableIfPrefilled = true;

  const isOptional = "optionalForPayment" in fieldDef && fieldDef.optionalForPayment;
  const fieldLabel = getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, fieldDef.name);

  const commonProps: any = {
    name: fieldDef.name,
    label: isOptional ? `${fieldLabel} (${t("optional", { ns: COMMON_NAMESPACE })})` : fieldLabel,
  };

  if (fieldDef.placeholder) {
    commonProps.placeholder = getLabelWithKrn(
      labelSourceWithKrn.labelsWithKrn,
      typeKrn,
      fieldDef.placeholder
    );
  }

  if ("editableIfPrefilled" in fieldDef) {
    isEditableIfPrefilled = Boolean(fieldDef.editableIfPrefilled);
  }

  commonProps.editableIfPrefilled = isEditableIfPrefilled;

  return (
    <div key={fieldDef.name} className="ez-pay-payable-row">
      {fieldDef.type === "NonblankText" && <NonBlankText {...commonProps} />}
      {fieldDef.type === "Integer" && <IntegerField {...commonProps} />}
      {fieldDef.type === "Boolean" && (
        <BooleanField
          name={fieldDef.name}
          labelYes={getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, "radio-label-yes")}
          labelNo={getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, "radio-label-no")}
        />
      )}
      {fieldDef.type === "Email" && <EmailAddress {...commonProps} />}
      {fieldDef.type === "PhoneNumber" && <PhoneNumber {...commonProps} />}
      {fieldDef.type === "Date" && (
        <DateField {...commonProps} min={fieldDef.min} max={fieldDef.max} />
      )}
      {fieldDef.type === "DateTime" && (
        <DateTimeField {...commonProps} min={fieldDef.min} max={fieldDef.max} />
      )}
      {fieldDef.type === "DollarAmount" && <AmountField {...commonProps} />}
      {fieldDef.type === "SelectOne" && (
        <SelectOne {...commonProps} options={fieldDef.options} />
      )}
      {fieldDef.type === "SelectMany" && (
        <SelectManyField {...commonProps} options={fieldDef.options} />
      )}

      {fieldDef.type === "Address" && (
        <AddressField
          name={fieldDef.name}
          streetAddressLabel={getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, "address")}
          localityLabel={t("city", { ns: COMMON_NAMESPACE })}
          regionLabel={t("state", { ns: COMMON_NAMESPACE })}
          postalCodeLabel={t("zip/postalCode", { ns: COMMON_NAMESPACE })}
          editableIfPrefilled={isEditableIfPrefilled}
        />
      )}
    </div>
  );
};
